import { reportFailure } from "./telemetry";
import type { FileEntry, FileOutbox, ImportResult } from "./file-import-state";
export class FileImportQueue {
  private entries: FileEntry[] = [];
  private files = new Map<string, File>();
  private listeners = new Set<() => void>();
  private controller?: AbortController;
  private disposed = false;
  constructor(
    private outbox: FileOutbox,
    private operations: {
      exists: (id: string) => boolean;
      process: (
        entry: FileEntry,
        file: File,
        signal: AbortSignal,
      ) => Promise<ImportResult>;
      repair: (entry: FileEntry, result: ImportResult) => void;
      editable: () => boolean;
    },
  ) {}
  getSnapshot = () => this.entries;
  subscribe = (f: () => void) => {
    this.listeners.add(f);
    return () => {
      this.listeners.delete(f);
    };
  };
  private publish() {
    this.entries = [...this.entries];
    this.listeners.forEach((f) => f());
  }
  private update(id: string, patch: Partial<FileEntry>) {
    this.entries = this.entries.map((e) =>
      e.id === id ? { ...e, ...patch } : e,
    );
    this.publish();
  }
  async restore() {
    const saved = await this.outbox.list();
    if (this.disposed) return;
    for (const e of saved) {
      if (this.operations.exists(e.id)) {
        await this.outbox.remove(e.id);
        continue;
      }
      if (!this.entries.some((item) => item.id === e.id))
        this.entries.push({
          ...e,
          status: "Paused",
          error: e.hasResult
            ? "Previously added object is missing. Resume restores it only when you choose."
            : undefined,
        });
    }
    this.publish();
  }
  add(entries: Array<{ entry: FileEntry; file: File }>) {
    const pending = this.entries.filter((e) => e.status !== "Added");
    if (
      pending.length + entries.length > 50 ||
      pending.reduce((n, e) => n + e.size, 0) +
        entries.reduce((n, e) => n + e.entry.size, 0) >
        150 * 1024 * 1024
    )
      throw new Error(
        "Keep pending files within 50 files and 150 MB. Remove failures or finish them first.",
      );
    for (const { entry, file } of entries) {
      this.files.set(entry.id, file);
      this.entries.push(entry);
    }
    this.publish();
  }
  configure(
    id: string,
    patch: Partial<Pick<FileEntry, "mode" | "page" | "worksheet" | "headers">>,
  ) {
    if (this.controller) return;
    this.update(id, { ...patch, error: undefined, status: "Ready" });
  }
  async remove(id: string) {
    if (this.controller) return;
    await this.outbox.remove(id);
    this.files.delete(id);
    this.entries = this.entries.filter((e) => e.id !== id);
    this.publish();
  }
  pause() {
    this.controller?.abort();
  }
  dispose() {
    this.disposed = true;
    this.pause();
    this.listeners.clear();
    this.files.clear();
  }
  isBusy() {
    return !!this.controller;
  }
  hasUnsaved() {
    return this.entries.some((e) => e.status !== "Added" && !e.saved);
  }
  async run(onlyIds?: Iterable<string>) {
    if (this.controller || this.disposed || !this.operations.editable()) return;
    const selected = onlyIds ? new Set(onlyIds) : null;
    const unfinished = () =>
      this.entries.filter(
        (entry) =>
          entry.status !== "Added" && (!selected || selected.has(entry.id)),
      );
    const controller = new AbortController();
    this.controller = controller;
    try {
      // Save queued originals in sequence before expensive conversion starts.
      for (const entry of unfinished()) {
        if (controller.signal.aborted || this.disposed) break;
        const file = this.files.get(entry.id);
        if (!file) continue;
        this.update(entry.id, { status: "Saving", error: undefined });
        try {
          await this.outbox.put({ ...entry, file });
          this.files.delete(entry.id);
          this.update(entry.id, { saved: true, status: "Ready" });
        } catch (error) {
          reportFailure("import", "files", error);
          this.update(entry.id, {
            status: "Failed",
            error:
              "Cannot save this file on this device. Free browser storage and retry.",
          });
        }
      }
      for (const initial of unfinished()) {
        if (controller.signal.aborted || this.disposed) break;
        const entry = this.entries.find((e) => e.id === initial.id)!;
        try {
          if (!this.operations.editable())
            throw new Error("Edit access was removed.");
          // Stable IDs make retries harmless if the previous creation already synced.
          if (this.operations.exists(entry.id)) {
            this.update(entry.id, { status: "Added", error: undefined });
            this.files.delete(entry.id);
            continue;
          }
          this.update(entry.id, { status: "Saving", error: undefined });
          const saved = await this.outbox.get(entry.id);
          controller.signal.throwIfAborted();
          if (!this.operations.editable())
            throw new Error("Edit access was removed.");
          let result = saved?.result;
          if (result) {
            this.operations.repair(entry, result);
          } else {
            const blob = this.files.get(entry.id) ?? saved?.file;
            if (!blob)
              throw new Error(
                "Saved source unavailable. Remove this entry and choose the file again.",
              );
            const file =
              blob instanceof File
                ? blob
                : new File([blob], entry.name, {
                    type: entry.mime,
                    lastModified: entry.modified,
                  });
            await this.outbox.put({ ...entry, file });
            this.files.delete(entry.id);
            this.update(entry.id, { saved: true, status: "Importing" });
            controller.signal.throwIfAborted();
            result = await this.operations.process(
              entry,
              file,
              controller.signal,
            );
          }
          // Keep a small receipt until a fresh server snapshot confirms the object.
          // Never recreate an absent/deleted object automatically on restore.
          await this.outbox.put({
            ...entry,
            status: "Added",
            saved: true,
            result,
          });
          this.update(entry.id, {
            status: "Added",
            saved: true,
            hasResult: true,
            error: undefined,
          });
        } catch (error) {
          if (!controller.signal.aborted)
            reportFailure("import", "files", error);
          this.update(entry.id, {
            status: controller.signal.aborted ? "Paused" : "Failed",
            error: controller.signal.aborted
              ? "Paused. Resume adds only unfinished files."
              : (error as Error).message,
          });
          if (controller.signal.aborted) break;
        }
      }
    } finally {
      this.controller = undefined;
      this.publish();
    }
  }
}
